import {useDebouncedCallback} from 'use-debounce';
import {store} from 'store/customer/storeSetup';
import {useAppSelector} from 'store/customer';
import {
    setIsAutoSaving,
    getIsAutoSaving,
    updateCabinetPosition,
    updatePlaceholderPosition,
    registerPlaceholderId,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {debounce} from 'lodash';
import {roomPlannerApi} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {
    getPlaceholderCopyIndex,
    getPlaceholderGroupId,
} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';

type PlaceholderUserData = {
    id?: string | number;
    isPlaceholder?: boolean;
    currentDirection?: string;
    placeholderFaceId?: number;
    placeholderName?: string;
    layoutPlaceholderId?: number;
};

/**
 * Creates still in flight, by mesh. A placement is created without an id and
 * addressed by the id the create returns, so a save made before that id is
 * back waits for it instead of creating the placement twice.
 */
const pendingCreates = new WeakMap<CabinetMesh, Promise<number | undefined>>();

/**
 * Layout placement id of a placeholder mesh, waiting for its create when one
 * is in flight. Undefined when the placement was never saved.
 * @param {CabinetMesh} cabinetGroup Placeholder mesh
 * @return {Promise<number | undefined>} Placement id
 */
export const resolveLayoutPlaceholderId = (
    cabinetGroup: CabinetMesh
): Promise<number | undefined> =>
    pendingCreates.get(cabinetGroup) ??
    Promise.resolve(
        (cabinetGroup.userData as PlaceholderUserData).layoutPlaceholderId
    );

/**
 * Persist placeholder pose to the layout API (same pattern as cabinets).
 * The first save creates the placement; later saves update it by id.
 * @param {CabinetMesh} cabinetGroup Mesh to persist
 * @param {boolean} skipHistory When true, update the stored pose but do not
 * push a move/rotate history entry (add, restore, undo apply).
 * @return {void}
 */
const savePlaceholderPosition = (
    cabinetGroup: CabinetMesh,
    skipHistory = false
) => {
    const layoutRoomId = store.getState().roomPlanner.layoutId;
    const userData = cabinetGroup.userData as PlaceholderUserData;
    const sceneId = String(userData.id ?? '');
    const groupId = getPlaceholderGroupId(cabinetGroup);
    const copyIndex = getPlaceholderCopyIndex(cabinetGroup);

    if (!sceneId) return;

    store.dispatch(registerPlaceholderId(sceneId));

    store.dispatch(
        updatePlaceholderPosition({
            rotation: cabinetGroup.rotation,
            position: cabinetGroup.position,
            id: sceneId,
            placeholderId: userData.placeholderFaceId,
            skipHistory,
            typeName: userData.placeholderName,
            currentDirection: userData.currentDirection ?? 'front',
            groupId,
            copyIndex,
        })
    );

    if (!layoutRoomId) return;

    const values = cabinetGroup.userData.values as
        | {
              cabinet_width?: number;
              cabinet_height?: number;
              cabinet_depth?: number;
          }
        | undefined;

    const pose = {
        position: cabinetGroup.position,
        rotation: cabinetGroup.rotation,
        current_direction: userData.currentDirection ?? 'front',
        width: Number(values?.cabinet_width) || undefined,
        height: Number(values?.cabinet_height) || undefined,
        depth: Number(values?.cabinet_depth) || undefined,
        ...(groupId ? {group_id: groupId, index: copyIndex} : {}),
    };
    const save = roomPlannerApi.endpoints.savePlaceholderPosition.initiate;

    store.dispatch(setIsAutoSaving(true));

    let request: Promise<unknown>;
    const pendingCreate = pendingCreates.get(cabinetGroup);

    if (userData.layoutPlaceholderId != null || pendingCreate !== undefined) {
        request = resolveLayoutPlaceholderId(cabinetGroup).then((id) =>
            id != null ? store.dispatch(save({id, ...pose})) : undefined
        );
    } else if (userData.placeholderFaceId != null) {
        const create = store
            .dispatch(
                save({
                    layout_room_id: layoutRoomId,
                    placeholder_id: userData.placeholderFaceId,
                    ...pose,
                })
            )
            .unwrap()
            .then(({layout_room_placeholder: placement}) => {
                userData.layoutPlaceholderId = placement.id;
                return placement.id;
            })
            .catch((): undefined => undefined)
            .finally(() => pendingCreates.delete(cabinetGroup));
        pendingCreates.set(cabinetGroup, create);
        request = create;
    } else {
        // Legacy fridge/oven configs have no catalogue entry to place from.
        request = Promise.resolve();
    }

    void request.finally(() => {
        setTimeout(() => {
            store.dispatch(setIsAutoSaving(false));
        }, 1000);
    });
};

export const autoSave = (
    cabinetGroup: CabinetMesh,
    skipHistory = false,
    checkPreview = true
) => {
    if (cabinetGroup.userData?.isPlaceholder) {
        savePlaceholderPosition(cabinetGroup, skipHistory);
        return;
    }
    const deletedIds = store.getState().roomPlanner.deletedIds;
    const id = Number(cabinetGroup.userData?.values?.id);

    if (deletedIds.includes(id)) {
        return;
    }

    if (checkPreview) {
        if (
            ['LEFT_LEVELED_STATIC', 'TOP'].includes(
                store.getState().roomPlanner.previewPosition
            )
        ) {
            return;
        }
    }

    let index = 0;

    if (id === undefined || isNaN(id)) return;
    store.dispatch(setIsAutoSaving(true));
    const saveCabinetPosition =
        roomPlannerApi.endpoints.saveCabinetPosition.initiate;

    if (cabinetGroup?.userData?.id != 'undefined') {
        [, index] = String(cabinetGroup.userData.id)
            .split('-')
            .map((val) => Number(val));
    }

    const values = cabinetGroup.userData?.values as
        {room_cab_number?: number; type_name?: string} | undefined;
    const roomIndex = store.getState().roomPlanner.roomIndex;

    // updating redux state early
    store.dispatch(
        updateCabinetPosition({
            rotation: cabinetGroup.rotation,
            position: cabinetGroup.position,
            id,
            index,
            skipHistory,
            roomIndex: roomIndex ?? undefined,
            roomCabNumber: values?.room_cab_number,
            typeName: values?.type_name,
        })
    );

    void store
        .dispatch(
            saveCabinetPosition({
                jobCabinetId: id,
                layout_room_id: store.getState().roomPlanner.layoutId,
                position: cabinetGroup.position,
                rotation: cabinetGroup.rotation,
                index,
            })
        )
        .then(() => {
            localStorage.setItem(
                `cabinet-currentDirection:${
                    (cabinetGroup.userData as {id: string}).id
                }`,
                JSON.stringify(cabinetGroup.userData.currentDirection)
            );
        })
        .finally(() => {
            setTimeout(() => {
                store.dispatch(setIsAutoSaving(false));
            }, 1000);
        });
};

export const debouncedAutoSave = debounce(autoSave, 100);

const useAutoSave = () => {
    const isAutoSaving = useAppSelector(getIsAutoSaving);
    const debouncedSave = useDebouncedCallback(autoSave, 100);

    return {isAutoSaving, debouncedSave};
};

export default useAutoSave;
